import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CancelIcon from '@mui/icons-material/Cancel';
import PaidIcon from '@mui/icons-material/Paid';
import ReplayIcon from '@mui/icons-material/Replay';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import LinkIcon from '@mui/icons-material/Link';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import AccountingDataTable from '../../accounting/shared/AccountingDataTable';
import type { DataTableColumn } from '../../accounting/shared/AccountingDataTable';
import BillingService, {
  RenewalCase,
  RenewalCaseCounts,
  RenewalCaseStatus,
} from '../../../services/BillingService';
import SuperAdminService, { type SuperAdminReminder, type SuperAdminReminderKind } from '../../../services/SuperAdminService';
import { useSuperAdminReminders } from '../../../contexts/SuperAdminRemindersContext';
import { useAuth } from '../../../contexts/AuthContext';
import CaseFormDialog from './CaseFormDialog';
import InvoicePaperPreviewDialog from './InvoicePaperPreviewDialog';
import { renewalPayUrl } from '../../../config/api';
import GuideButton from '../../common/guide/GuideButton';

const STATUS_META: Record<RenewalCaseStatus, { label: string; color: 'default' | 'info' | 'success' | 'warning' }> = {
  Draft: { label: 'Taslak', color: 'default' },
  Active: { label: 'Yayında', color: 'warning' },
  Paid: { label: 'Ödendi', color: 'success' },
  Cancelled: { label: 'İptal', color: 'default' },
};

const paymentUrl = (token: string) => renewalPayUrl(token);

/** Fatura otomasyonu durum rozeti meta bilgisi. */
const INVOICE_META: Record<string, { label: string; color: 'default' | 'info' | 'success' | 'warning' | 'error' }> = {
  Pending: { label: 'Faturaya hazır', color: 'warning' },
  Invoiced: { label: 'Fatura kesildi', color: 'info' },
  Sent: { label: 'E-fatura gönderildi', color: 'info' },
  Emailed: { label: 'Gönderildi', color: 'success' },
  Error: { label: 'Hata', color: 'error' },
};

const INVOICE_OPEN_STATUSES = new Set(['Invoiced', 'Sent', 'Emailed']);

const fmtDateTime = (v?: string | null) => {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

interface CasesTabProps {
  /** Ödenenler sekmesi: yalnızca Paid kayıtlar. */
  mode?: 'open' | 'paid';
}

const CasesTab: React.FC<CasesTabProps> = ({ mode = 'open' }) => {
  const [rows, setRows] = React.useState<RenewalCase[]>([]);
  const [counts, setCounts] = React.useState<RenewalCaseCounts>({ draft: 0, active: 0, paid: 0, cancelled: 0 });
  const [loading, setLoading] = React.useState(false);
  const [statusFilter, setStatusFilter] = React.useState<string>('');
  const [sortKey, setSortKey] = React.useState<'id' | 'company' | 'expiry' | 'amount' | 'paidAt'>(mode === 'paid' ? 'paidAt' : 'id');
  const [sortDir, setSortDir] = React.useState<'asc' | 'desc'>('desc');
  const [formOpen, setFormOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<RenewalCase | null>(null);
  const [prefill, setPrefill] = React.useState<SuperAdminReminder | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const { refresh: refreshReminders } = useSuperAdminReminders();
  const [confirm, setConfirm] = React.useState<{ action: string; row: RenewalCase } | null>(null);
  const [toast, setToast] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [sendingId, setSendingId] = React.useState<number | null>(null);
  const [previewId, setPreviewId] = React.useState<number | null>(null);
  const navigate = useNavigate();
  const { userDetails, impersonate } = useAuth();
  const [issuerLinks, setIssuerLinks] = React.useState<Record<number, string>>({});
  const issuerLinksRef = React.useRef(issuerLinks);
  issuerLinksRef.current = issuerLinks;
  const companyIdRef = React.useRef(userDetails?.companyId);
  companyIdRef.current = userDetails?.companyId;
  const [goConfirm, setGoConfirm] = React.useState<RenewalCase | null>(null);
  const [openingId, setOpeningId] = React.useState<number | null>(null);
  const activatingIds = React.useRef(new Set<number>());

  React.useEffect(() => {
    BillingService.getIssuers(true)
      .then((list) => {
        const map: Record<number, string> = {};
        list.forEach((i) => {
          if (i.linkedCompanyId) map[i.id] = i.linkedCompanyId;
        });
        setIssuerLinks(map);
      })
      .catch(() => setIssuerLinks({}));
  }, []);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const query = mode === 'paid'
        ? { status: 'Paid' as const }
        : { status: statusFilter || undefined, excludePaid: !statusFilter };
      const res = await BillingService.getCases(query);
      let list = mode === 'open' && !statusFilter
        ? res.data.filter((r) => r.status !== 'Paid')
        : res.data;
      let nextCounts = res.counts;

      // Taslak ayrı bir adım beklemez; duyuru ve e-posta kendiliğinden gider.
      const drafts = mode === 'open'
        ? list.filter((r) => r.status === 'Draft' && !activatingIds.current.has(r.id))
        : [];
      drafts.forEach((r) => activatingIds.current.add(r.id));
      if (drafts.length > 0) {
        try {
          await Promise.all(drafts.map((r) => BillingService.activateCase(r.id)));
          const refreshed = await BillingService.getCases(query);
          list = mode === 'open' && !statusFilter
            ? refreshed.data.filter((r) => r.status !== 'Paid')
            : refreshed.data;
          nextCounts = refreshed.counts;
          setToast(drafts.length === 1
            ? 'Taslak kayıt yayınlandı.'
            : `${drafts.length} taslak kayıt yayınlandı.`);
        } catch (activateError) {
          drafts.forEach((r) => activatingIds.current.delete(r.id));
          throw activateError;
        }
      }

      setRows(list);
      setCounts(nextCounts);
    } catch (e: any) {
      setError(e?.message || 'Tahsilatlar yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, mode]);

  React.useEffect(() => {
    load();
  }, [load]);

  React.useEffect(() => {
    const kind = searchParams.get('prefill');
    const companyId = searchParams.get('companyId');
    if (!kind || !companyId) return;
    setEditing(null);
    setPrefill({
      kind: kind as SuperAdminReminderKind,
      key: searchParams.get('key') || `subscription:${companyId}`,
      companyId,
      title: searchParams.get('title') || '',
      subtitle: null,
      detail: null,
      expiryDate: searchParams.get('expiry'),
      daysRemaining: 0,
      searchText: null,
      targetTab: 'billing',
    });
    setFormOpen(true);
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  const runAction = async () => {
    if (!confirm) return;
    const { action, row } = confirm;
    try {
      if (action === 'cancel') await BillingService.cancelCase(row.id);
      else if (action === 'mark-paid') await BillingService.markPaid(row.id);
      else if (action === 'repeat') {
        const created = await BillingService.repeatCase(row.id);
        if (created.status === 'Draft' || created.status === 'Cancelled') {
          await BillingService.activateCase(created.id);
        }
      } else if (action === 'delete') await BillingService.deleteCase(row.id);
      setToast(action === 'repeat' ? 'Aynı kalemlerle yeni tahsilat açıldı ve yayınlandı.' : 'İşlem tamamlandı.');
      setConfirm(null);
      await load();
      refreshReminders();
    } catch (e: any) {
      setError(e?.message || 'İşlem başarısız.');
      setConfirm(null);
    }
  };

  const sendInvoice = async (row: RenewalCase) => {
    setSendingId(row.id);
    try {
      const res = await BillingService.sendInvoice(row.id);
      if (res.success) {
        setToast(res.message || 'Fatura kesildi ve fatura şirketinin fatura listesine eklendi.');
        setPreviewId(null);
      } else {
        setError(res.message || 'Fatura gönderilemedi.');
      }
      await load();
    } catch (e: any) {
      setError(e?.message || 'Fatura gönderilemedi.');
    } finally {
      setSendingId(null);
    }
  };

  const openIssuerInvoices = async (row: RenewalCase) => {
    const linkedId = row.issuerCompanyId ? issuerLinks[row.issuerCompanyId] : '';
    if (!linkedId) {
      setError('Fatura şirketi bir panel şirketine bağlı değil.');
      setGoConfirm(null);
      return;
    }
    const path = row.invoiceId
      ? `/fatura/satis?invoiceId=${encodeURIComponent(row.invoiceId)}`
      : '/fatura/satis';
    if (userDetails?.companyId === linkedId) {
      setGoConfirm(null);
      navigate(path);
      return;
    }
    setOpeningId(row.id);
    try {
      const users = await SuperAdminService.getInstance().getCompanyUsers(linkedId);
      const admin = users.find((u) => (u.role || '').toLowerCase() === 'admin') ?? users[0];
      if (!admin) {
        setError('Fatura şirketinde geçilecek bir kullanıcı yok.');
        return;
      }
      await impersonate({ targetUserId: admin.id });
      setGoConfirm(null);
      navigate(path);
    } catch (e: any) {
      setError(e?.message || 'Fatura şirketine geçilemedi.');
    } finally {
      setOpeningId(null);
    }
  };

  const copyLink = async (row: RenewalCase) => {
    try {
      await navigator.clipboard.writeText(paymentUrl(row.paymentToken));
      setToast('Ödeme linki kopyalandı.');
    } catch {
      setError('Link kopyalanamadı.');
    }
  };

  const visibleRows = React.useMemo(() => {
    const list = [...rows];
    const dir = sortDir === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      if (sortKey === 'company') return dir * (a.companyName || '').localeCompare(b.companyName || '', 'tr');
      if (sortKey === 'expiry') return dir * ((a.expiryDate || '').localeCompare(b.expiryDate || ''));
      if (sortKey === 'amount') return dir * (a.totalAmount - b.totalAmount);
      if (sortKey === 'paidAt') return dir * ((a.paidAt || '').localeCompare(b.paidAt || ''));
      return dir * (a.id - b.id);
    });
    return list;
  }, [rows, sortKey, sortDir]);

  const requestOpenInvoices = (row: RenewalCase) => {
    const linkedId = row.issuerCompanyId ? issuerLinksRef.current[row.issuerCompanyId] : '';
    const path = row.invoiceId
      ? `/fatura/satis?invoiceId=${encodeURIComponent(row.invoiceId)}`
      : '/fatura/satis';
    if (linkedId && companyIdRef.current === linkedId) {
      navigate(path);
      return;
    }
    setGoConfirm(row);
  };

  const columns = React.useMemo<DataTableColumn<RenewalCase>[]>(() => [
    { id: 'sn', label: 'SN', width: '56px', align: 'center', render: (_row, index) => index + 1 },
    {
      id: 'company',
      label: 'Müşteri',
      minWidth: '180px',
      render: (r) => r.companyName || r.companyId,
    },
    { id: 'branch', label: 'Şube', render: (r) => (r.branchId ? r.branchName || r.branchId : 'Şirket geneli') },
    { id: 'franchise', label: 'Franchise', render: (r) => (r.isFranchise ? r.firmName || '—' : '—') },
    {
      id: 'expiry',
      label: 'Son Tarih',
      render: (r) => (r.expiryDate ? new Date(r.expiryDate).toLocaleDateString('tr-TR') : '—'),
    },
    {
      id: 'amount',
      label: 'Tutar',
      align: 'right',
      render: (r) => `${r.totalAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ${r.currency}`,
    },
    { id: 'issuer', label: 'Fatura Şirketi', render: (r) => r.issuerCompanyTitle || '—' },
    { id: 'email', label: 'E-posta', minWidth: '160px', render: (r) => r.notifyEmail || '—' },
    {
      id: 'status',
      label: 'Durum',
      render: (r) => STATUS_META[r.status].label,
    },
    ...(mode === 'paid'
      ? [{
          id: 'paidAt',
          label: 'Ödeme zamanı',
          minWidth: '150px',
          render: (r: RenewalCase) => fmtDateTime(r.paidAt),
        } satisfies DataTableColumn<RenewalCase>,
        {
          id: 'invoice',
          label: 'Fatura',
          minWidth: '220px',
          cellSx: { overflow: 'visible', textOverflow: 'unset' },
          render: (r: RenewalCase) => {
            const meta = r.invoiceStatus ? INVOICE_META[r.invoiceStatus] : null;
            if (!meta) return <Typography variant="caption" color="text.secondary">—</Typography>;
            const chip = <Chip size="small" color={meta.color} label={r.invoiceNumber ? `${meta.label} · ${r.invoiceNumber}` : meta.label} />;
            const statusChip = r.invoiceStatus === 'Error' && r.invoiceError
              ? <Tooltip title={r.invoiceError}>{chip}</Tooltip>
              : chip;
            return statusChip;
          },
        } satisfies DataTableColumn<RenewalCase>]
      : []),
    {
      id: 'actions',
      label: 'İşlemler',
      width: '200px',
      align: 'right',
      cellSx: { overflow: 'visible', textOverflow: 'unset' },
      render: (r) => (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', overflow: 'visible' }} onClick={(e) => e.stopPropagation()}>
          <Tooltip title="Ödeme linkini kopyala">
            <IconButton size="small" onClick={() => copyLink(r)}>
              <LinkIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {(r.status === 'Draft' || r.status === 'Active') && (
            <Tooltip title="Ödendi işaretle">
              <IconButton size="small" color="success" onClick={() => setConfirm({ action: 'mark-paid', row: r })}>
                <PaidIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {r.status === 'Active' && (
            <Tooltip title="İptal et">
              <IconButton size="small" onClick={() => setConfirm({ action: 'cancel', row: r })}>
                <CancelIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {r.status === 'Paid'
            && (!r.invoiceStatus || r.invoiceStatus === 'Pending' || r.invoiceStatus === 'Error') && (
            <Tooltip title="Fatura önizle ve kes">
              <span>
                <IconButton
                  size="small"
                  color={r.invoiceStatus === 'Error' ? 'warning' : 'success'}
                  disabled={sendingId === r.id}
                  onClick={() => setPreviewId(r.id)}
                >
                  {sendingId === r.id ? <CircularProgress size={16} /> : <ReceiptLongIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          )}
          {r.status === 'Paid' && !!r.invoiceStatus && INVOICE_OPEN_STATUSES.has(r.invoiceStatus) && (
            <Tooltip title="Fatura şirketinin faturalarına git">
              <span>
                <IconButton size="small" color="primary" disabled={openingId === r.id} onClick={() => requestOpenInvoices(r)}>
                  {openingId === r.id ? <CircularProgress size={16} /> : <OpenInNewIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          )}
          {r.status === 'Paid' && (
            <Tooltip title="Aynı kalemlerle tekrarla">
              <IconButton size="small" color="primary" onClick={() => setConfirm({ action: 'repeat', row: r })}>
                <ReplayIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {r.status !== 'Paid' && (
            <Tooltip title="Düzenle">
              <IconButton size="small" color="primary" onClick={() => { setEditing(r); setFormOpen(true); }}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Sil">
            <IconButton size="small" color="error" onClick={() => setConfirm({ action: 'delete', row: r })}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [mode, sendingId, openingId]);

  const confirmText = (action: string) => {
    switch (action) {
      case 'cancel':
        return 'Bu kayıt iptal edilecek ve varsa duyuru kapatılacak. Onaylıyor musunuz?';
      case 'mark-paid':
        return 'Bu kayıt manuel olarak ödendi işaretlenecek ve duyuru kapatılacak. Onaylıyor musunuz?';
      case 'repeat':
        return 'Ödenmiş kaydın kalemleri yeni bir tahsilat olarak açılacak ve müşteri ekranlarında duyuru yayınlanacak. Onaylıyor musunuz?';
      case 'delete':
        return 'Bu kayıt silinecek. Onaylıyor musunuz?';
      default:
        return 'Onaylıyor musunuz?';
    }
  };

  return (
    <Box>
      {formOpen && (
        <CaseFormDialog
          open
          editing={editing}
          prefill={prefill}
          onClose={() => {
            setFormOpen(false);
            setPrefill(null);
          }}
          onSaved={() => {
            setToast('Tahsilat kaydı kaydedildi.');
            setPrefill(null);
            load();
            refreshReminders();
          }}
        />
      )}

      {!formOpen && (
      <>
      <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        {mode === 'open' && (
        <TextField
          select
          size="small"
          label="Durum"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          sx={{ width: { xs: '100%', sm: 160 } }}
        >
          <MenuItem value="">Açık kayıtlar</MenuItem>
          <MenuItem value="Draft">Taslak ({counts.draft})</MenuItem>
          <MenuItem value="Active">Yayında ({counts.active})</MenuItem>
          <MenuItem value="Cancelled">İptal ({counts.cancelled})</MenuItem>
        </TextField>
        )}
        <TextField
          select
          size="small"
          label="Sırala"
          value={sortKey}
          onChange={(e) => setSortKey(e.target.value as typeof sortKey)}
          sx={{ width: { xs: '100%', sm: 160 } }}
        >
          <MenuItem value="id">Kayıt no</MenuItem>
          <MenuItem value="company">Müşteri</MenuItem>
          <MenuItem value="expiry">Son tarih</MenuItem>
          <MenuItem value="amount">Tutar</MenuItem>
          {mode === 'paid' && <MenuItem value="paidAt">Ödeme zamanı</MenuItem>}
        </TextField>
        <IconButton size="small" onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))} aria-label="Sıra yönü">
          {sortDir === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
        </IconButton>
        <Box sx={{ flex: 1 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: '#1976d2', color: 'white', px: 2, py: 1, borderRadius: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {mode === 'paid' ? `${visibleRows.length} ödeme` : `${visibleRows.length} tahsilat`}
          </Typography>
        </Box>
        {mode === 'open' && (
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setEditing(null);
            setFormOpen(true);
          }}
          sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
        >
          Yeni Tahsilat
        </Button>
        )}
        <GuideButton guideKey="superadmin.billingCases" />
      </Paper>

      <AccountingDataTable<RenewalCase>
        columns={columns}
        rows={visibleRows}
        getRowId={(r) => String(r.id)}
        loading={loading}
        ariaLabel="Tahsilatlar"
        emptyMessage={mode === 'paid' ? 'Henüz ödenmiş tahsilat yok.' : 'Kayıt bulunamadı.'}
        defaultRowsPerPage={25}
        rowsPerPageOptions={[25, 50, 100]}
        onRowClick={(r) => {
          if (r.status === 'Paid') return;
          setEditing(r);
          setFormOpen(true);
        }}
        clickable
      />
      </>
      )}

      <InvoicePaperPreviewDialog
        caseId={previewId}
        onClose={() => setPreviewId(null)}
        sending={sendingId != null}
        onSend={() => {
          const row = rows.find((r) => r.id === previewId);
          if (row) sendInvoice(row);
        }}
      />

      <Dialog open={!!goConfirm} onClose={() => openingId == null && setGoConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Faturalara git</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {goConfirm?.issuerCompanyTitle || 'Fatura şirketi'} satış faturalarına geçilecek.
            Oturum bu şirketin yöneticisine alınır. Süper admin paneline sağ alttaki dönüş düğmesinden dönebilirsiniz.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGoConfirm(null)} disabled={openingId != null}>Vazgeç</Button>
          <Button
            variant="contained"
            disabled={openingId != null}
            onClick={() => goConfirm && openIssuerInvoices(goConfirm)}
          >
            {openingId != null ? 'Geçiliyor...' : 'Faturalara git'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirm} onClose={() => setConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Onay</DialogTitle>
        <DialogContent>{confirm ? confirmText(confirm.action) : ''}</DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>Vazgeç</Button>
          <Button
            variant="contained"
            color={confirm?.action === 'delete' ? 'error' : 'primary'}
            onClick={runAction}
          >
            Onayla
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast(null)}>
        <Alert severity="success" onClose={() => setToast(null)}>
          {toast}
        </Alert>
      </Snackbar>
      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CasesTab;
